import React, { useState } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  Typography,
  Tooltip,
  List,
  ListItem,
  ListItemButton,
  Grow,
  Slide,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import DriveFileRenameOutlineIcon from "@mui/icons-material/DriveFileRenameOutline";

// Side panel listing the user's chats, with per-chat rename/delete actions
// and the "New Chat" button. The parent owns the chat list and API calls;
// this component owns the transient UI state (menus, inline rename, dialog).
const ChatHistorySidebar = ({
  open,
  isFullscreen,
  userChats,
  selectedChatId,
  onSelectChat,
  onAddNewChat,
  onConfirmDelete,
  onConfirmRename,
}) => {
  // Chat context menu (delete / rename)
  const [chatMenuAnchor, setChatMenuAnchor] = useState(null);
  const [chatMenuTargetId, setChatMenuTargetId] = useState(null);
  // Inline rename
  const [editingChatId, setEditingChatId] = useState(null);
  const [editingChatName, setEditingChatName] = useState("");
  // Delete confirmation
  const [deleteConfirmChatId, setDeleteConfirmChatId] = useState(null);

  const handleOpenChatMenu = (e, chatId) => {
    e.stopPropagation();
    setChatMenuAnchor(e.currentTarget);
    setChatMenuTargetId(chatId);
  };

  const handleCloseChatMenu = () => {
    setChatMenuAnchor(null);
    setChatMenuTargetId(null);
  };

  const handleDeleteChat = () => {
    setDeleteConfirmChatId(chatMenuTargetId);
    handleCloseChatMenu();
  };

  const handleConfirmDelete = async () => {
    const chatId = deleteConfirmChatId;
    setDeleteConfirmChatId(null);
    await onConfirmDelete(chatId);
  };

  const handleStartRename = () => {
    const chat = userChats.find((c) => c.id === chatMenuTargetId);
    setEditingChatId(chatMenuTargetId);
    setEditingChatName(chat?.name || "");
    handleCloseChatMenu();
  };

  const handleConfirmRename = async (chatId) => {
    const trimmed = editingChatName.trim();
    if (!trimmed) {
      setEditingChatId(null);
      return;
    }
    try {
      await onConfirmRename(chatId, trimmed);
    } finally {
      setEditingChatId(null);
    }
  };

  const handleCancelRename = () => {
    setEditingChatId(null);
  };

  if (!open) {
    return null;
  }

  return (
    <Slide direction="right" in={open} timeout={300}>
      <Box
        sx={{
          width: isFullscreen ? "280px" : "35%",
          minWidth: isFullscreen ? "280px" : "150px",
          maxWidth: "280px",
          borderRight: "1px solid #e8eaf6",
          display: "flex",
          flexDirection: "column",
          position: "relative",
          background: "linear-gradient(180deg, #f5f3ff 0%, #fafafe 100%)",
          transition: "all 0.3s ease",
        }}
      >
        <Box
          sx={{
            padding: { xs: "12px 10px", sm: "16px 14px" },
            borderBottom: "1px solid #ede9fe",
            background: "linear-gradient(135deg, #ede9fe 0%, #f5f3ff 100%)",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Box
              sx={{
                width: 6,
                height: 6,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                flexShrink: 0,
                display: isFullscreen ? "block" : { xs: "none", sm: "block" },
              }}
            />
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "#6366f1",
                fontSize: { xs: "0.65rem", sm: "0.72rem" },
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                display: isFullscreen ? "block" : { xs: "none", sm: "block" },
              }}
            >
              Conversations
            </Typography>
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 700,
                color: "#6366f1",
                fontSize: "0.65rem",
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                display: isFullscreen ? "none" : { xs: "block", sm: "none" },
                textAlign: "center",
                width: "100%",
              }}
            >
              Chats
            </Typography>
          </Box>
        </Box>

        <List
          sx={{
            flexGrow: 1,
            overflowY: "auto",
            padding: "8px",
            paddingBottom: "64px",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-track": { background: "transparent" },
            "&::-webkit-scrollbar-thumb": { background: "#ddd6fe", borderRadius: "4px" },
          }}
        >
          {userChats.map((chat, index) => (
            <Grow key={chat.id} in={true} timeout={300 + index * 100}>
              <ListItem
                disablePadding
                sx={{ marginBottom: "4px", position: "relative", "&:hover .chat-actions": { opacity: 1 } }}
              >
                {editingChatId === chat.id ? (
                  /* ── Inline rename input ── */
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      width: "100%",
                      px: 1,
                      pt: 1,
                      pb: 1,
                      gap: 0.75,
                    }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <TextField
                      autoFocus
                      size="small"
                      fullWidth
                      value={editingChatName}
                      onChange={(e) => setEditingChatName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") handleConfirmRename(chat.id);
                        if (e.key === "Escape") handleCancelRename();
                      }}
                      inputProps={{ maxLength: 200 }}
                      sx={{
                        "& .MuiInputBase-root": {
                          backgroundColor: "#fff",
                          borderRadius: "8px",
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "0.85rem",
                          py: "8px",
                          color: "#1e293b",
                        },
                        "& .MuiOutlinedInput-notchedOutline": { borderColor: "#c7d2fe" },
                        "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#6366f1" },
                      }}
                    />
                    <Box sx={{ display: "flex", gap: 0.75 }}>
                      <Button
                        size="small"
                        variant="contained"
                        onClick={() => handleConfirmRename(chat.id)}
                        sx={{
                          flex: 1,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          fontWeight: 600,
                          borderRadius: "8px",
                          py: "5px",
                          background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
                          boxShadow: "none",
                          "&:hover": { background: "linear-gradient(135deg, #4f46e5, #7c3aed)", boxShadow: "none" },
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        size="small"
                        onClick={handleCancelRename}
                        sx={{
                          flex: 1,
                          textTransform: "none",
                          fontSize: "0.75rem",
                          borderRadius: "8px",
                          py: "5px",
                          color: "#94a3b8",
                          "&:hover": { backgroundColor: "#f1f5f9", color: "#64748b" },
                        }}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <>
                    <ListItemButton
                      selected={chat.id === selectedChatId}
                      onClick={() => onSelectChat(chat.id)}
                      sx={{
                        borderRadius: "10px",
                        padding: isFullscreen ? "9px 12px" : { xs: "7px 6px", sm: "9px 12px" },
                        minHeight: { xs: "38px", sm: "42px" },
                        pr: "32px",
                        "&.Mui-selected": {
                          backgroundColor: "#ede9fe",
                          borderLeft: "3px solid #6366f1",
                          boxShadow: "0 2px 8px rgba(99,102,241,0.12)",
                          "&:hover": { backgroundColor: "#e0d9fd" },
                        },
                        "&:hover": { backgroundColor: "rgba(99,102,241,0.07)" },
                        transition: "all 0.15s ease",
                      }}
                    >
                      <Tooltip title={chat.name} placement="right" sx={{ display: isFullscreen ? "none" : { xs: "block", sm: "none" } }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: chat.id === selectedChatId ? 600 : 400,
                            whiteSpace: isFullscreen ? "normal" : "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            width: "100%",
                            fontSize: { xs: "0.75rem", sm: "0.83rem" },
                            color: chat.id === selectedChatId ? "#4338ca" : "#64748b",
                            textAlign: isFullscreen ? "left" : { xs: "center", sm: "left" },
                          }}
                        >
                          {chat.name}
                        </Typography>
                      </Tooltip>
                    </ListItemButton>
                    {!chat.id?.startsWith("temp-") && (
                      <IconButton
                        className="chat-actions"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleOpenChatMenu(e, chat.id);
                        }}
                        sx={{
                          position: "absolute",
                          right: 4,
                          top: "50%",
                          transform: "translateY(-50%)",
                          opacity: 0,
                          transition: "opacity 0.15s",
                          color: "#a5b4fc",
                          padding: "3px",
                          zIndex: 1,
                          "&:hover": { color: "#6366f1", backgroundColor: "rgba(99,102,241,0.1)", borderRadius: "6px" },
                        }}
                      >
                        <MoreVertIcon sx={{ fontSize: "1rem" }} />
                      </IconButton>
                    )}
                  </>
                )}
              </ListItem>
            </Grow>
          ))}
        </List>

        {/* Context menu for chat actions */}
        <Menu
          anchorEl={chatMenuAnchor}
          open={Boolean(chatMenuAnchor)}
          onClose={handleCloseChatMenu}
          style={{ zIndex: 1100000 }}
          PaperProps={{
            sx: {
              minWidth: 155,
              backgroundColor: "#ffffff",
              border: "1px solid #ede9fe",
              boxShadow: "0 8px 24px rgba(99,102,241,0.15)",
              borderRadius: "12px",
            },
          }}
        >
          <MenuItem
            onClick={handleStartRename}
            sx={{ fontSize: "0.83rem", gap: 1.5, color: "#4338ca", borderRadius: "8px", mx: 0.5, my: 0.25, "&:hover": { backgroundColor: "#ede9fe" } }}
          >
            <DriveFileRenameOutlineIcon sx={{ fontSize: "1rem", color: "#6366f1" }} />
            Rename
          </MenuItem>
          <MenuItem
            onClick={handleDeleteChat}
            sx={{ fontSize: "0.83rem", gap: 1.5, color: "#ef4444", borderRadius: "8px", mx: 0.5, my: 0.25, "&:hover": { backgroundColor: "#fef2f2" } }}
          >
            <DeleteIcon sx={{ fontSize: "1rem" }} />
            Delete
          </MenuItem>
        </Menu>

        {/* Delete confirmation dialog */}
        <Dialog
          open={Boolean(deleteConfirmChatId)}
          onClose={() => setDeleteConfirmChatId(null)}
          style={{ zIndex: 1200000 }}
          PaperProps={{
            sx: {
              backgroundColor: "#ffffff",
              border: "1px solid #fecaca",
              borderRadius: "16px",
              boxShadow: "0 20px 60px rgba(239,68,68,0.12)",
              minWidth: "310px",
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 700, fontSize: "1rem", color: "#111827", pb: 0.5 }}>
            Delete conversation?
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ fontSize: "0.85rem", color: "#6b7280", lineHeight: 1.65 }}>
              This permanently removes the chat and all its messages. This cannot be undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions sx={{ px: 2.5, pb: 2.5, gap: 1 }}>
            <Button
              onClick={() => setDeleteConfirmChatId(null)}
              size="small"
              sx={{
                textTransform: "none",
                color: "#6b7280",
                borderRadius: "8px",
                px: 2,
                "&:hover": { backgroundColor: "#f9fafb" },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDelete}
              variant="contained"
              size="small"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 2,
                backgroundColor: "#ef4444",
                fontWeight: 600,
                fontSize: "0.83rem",
                boxShadow: "0 2px 8px rgba(239,68,68,0.3)",
                "&:hover": { backgroundColor: "#dc2626", boxShadow: "0 4px 12px rgba(239,68,68,0.4)" },
              }}
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        {/* New Chat Button */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            padding: "10px 10px",
            borderTop: "1px solid #ede9fe",
            background: "linear-gradient(0deg, #f5f3ff 0%, transparent 100%)",
          }}
        >
          <Button
            fullWidth
            disabled={selectedChatId?.startsWith("temp-")}
            onClick={onAddNewChat}
            startIcon={<AddIcon sx={{ fontSize: "1rem" }} />}
            sx={{
              background: "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)",
              color: "white",
              border: "none",
              borderRadius: "10px",
              textTransform: "none",
              fontSize: "0.8rem",
              fontWeight: 600,
              padding: "8px 12px",
              boxShadow: "0 2px 12px rgba(99,102,241,0.3)",
              justifyContent: isFullscreen ? "flex-start" : { xs: "center", sm: "flex-start" },
              "&:hover": {
                background: "linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)",
                boxShadow: "0 4px 16px rgba(99,102,241,0.4)",
              },
              "&:disabled": { background: "#e2e8f0", color: "#94a3b8", boxShadow: "none" },
              transition: "all 0.2s ease",
            }}
          >
            <Box sx={{ display: isFullscreen ? "block" : { xs: "none", sm: "block" } }}>New Chat</Box>
          </Button>
        </Box>
      </Box>
    </Slide>
  );
};

export default ChatHistorySidebar;
